import { useCallback } from "react";
import { useSearchParams } from "react-router";

/*
 * Several setters may run in one event ("set the search, clear the page"). React Router's functional
 * setSearchParams builds each update from the parameters of the last render, so the second call would undo the
 * first (#159). The pending draft below is shared by every setter in the same tick: each applies its change to the
 * draft, and one navigation carries all of them.
 */
let pending: { draft: URLSearchParams; flush: () => void } | null = null;

/**
 * Persists a piece of UI state (filter, tab, selected record, page) in the URL so screens are shareable and reload-safe.
 * Returns the current value (or the default) and a setter that removes the key when set to the default or undefined.
 * Setters called in the same tick are applied together, in one navigation.
 */
export function useSearchParamState(
  key: string,
  defaultValue = "",
): [string, (next: string | undefined) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;
  const set = useCallback(
    (next: string | undefined) => {
      const draft = pending?.draft ?? new URLSearchParams(params);
      if (next === undefined || next === "" || next === defaultValue) draft.delete(key);
      else draft.set(key, next);
      if (pending === null) {
        const flush = () => {
          if (pending?.draft !== draft) return;
          pending = null;
          setParams(draft, { replace: true });
        };
        pending = { draft, flush };
        queueMicrotask(flush);
      }
    },
    [key, defaultValue, params, setParams],
  );
  return [value, set];
}
